export type TrustPilotLoadState = 'pending' | 'loading' | 'ready' | 'error';

const TRUSTPILOT_SCRIPT_SRC =
  'https://widget.trustpilot.com/bootstrap/v5/tp.widget.bootstrap.min.js';

const RENDER_TIMEOUT_MS = 15_000;
const TRUSTPILOT_POLL_MS = 50;

declare global {
  interface Window {
    Trustpilot?: {
      loadFromElement: (element: HTMLElement, force?: boolean) => void;
    };
  }
}

function getTrustPilotScript(): HTMLScriptElement | null {
  return document.querySelector(`script[src="${TRUSTPILOT_SCRIPT_SRC}"]`);
}

function waitForTrustpilotGlobal(timeoutMs = RENDER_TIMEOUT_MS): Promise<void> {
  if (window.Trustpilot) {
    return Promise.resolve();
  }

  return new Promise((resolve, reject) => {
    const startedAt = Date.now();

    const tick = () => {
      if (window.Trustpilot) {
        resolve();
        return;
      }

      if (Date.now() - startedAt >= timeoutMs) {
        reject(new Error('TrustPilot global is not available'));
        return;
      }

      window.setTimeout(tick, TRUSTPILOT_POLL_MS);
    };

    tick();
  });
}

function loadTrustPilotScript(): Promise<void> {
  const existing = getTrustPilotScript();

  if (existing) {
    return waitForTrustpilotGlobal();
  }

  return new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = TRUSTPILOT_SCRIPT_SRC;
    script.async = true;
    script.type = 'text/javascript';
    script.dataset.trustpilotBootstrap = 'true';
    script.onload = () => waitForTrustpilotGlobal().then(resolve).catch(reject);
    script.onerror = () => reject(new Error('TrustPilot script failed to load'));
    document.head.appendChild(script);
  });
}

function waitForWidgetRender(
  element: HTMLElement,
  timeoutMs = RENDER_TIMEOUT_MS,
): Promise<void> {
  if (element.querySelector('iframe')) {
    return Promise.resolve();
  }

  return new Promise((resolve, reject) => {
    const observer = new MutationObserver(() => {
      if (element.querySelector('iframe')) {
        cleanup();
        resolve();
      }
    });

    const timer = window.setTimeout(() => {
      cleanup();
      reject(new Error('TrustPilot widget render timeout'));
    }, timeoutMs);

    function cleanup() {
      observer.disconnect();
      window.clearTimeout(timer);
    }

    observer.observe(element, { childList: true, subtree: true });
  });
}

export function useTrustPilotWidget(element: Ref<HTMLElement | null>) {
  const loadState = ref<TrustPilotLoadState>('pending');
  let initialized = false;

  async function initWidget() {
    const widget = element.value;
    if (!widget || initialized) {
      return;
    }

    initialized = true;
    loadState.value = 'loading';

    try {
      await loadTrustPilotScript();
      window.Trustpilot!.loadFromElement(widget, true);
      await waitForWidgetRender(widget);
      loadState.value = 'ready';
    } catch {
      loadState.value = 'error';
      initialized = false;
    }
  }

  watch(
    element,
    (widget) => {
      if (widget) {
        nextTick(() => initWidget());
      }
    },
    { flush: 'post', immediate: true },
  );

  return {
    loadState: readonly(loadState),
    initWidget,
  };
}
